import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
// If you have a logo, uncomment and fix the path:
import logo from "../../assets/Main_Logo_AI_Colored_Padding.png";

// ---- Footer metrics (keep these in sync with footer style)
const FOOTER_HEIGHT = 24;
const FOOTER_MARGIN = 16;

// ---- Design tokens
const C = {
  green: "#14833b",
  greenDark: "#2e7d32",
  red: "#c62828",
  gray100: "#f5f6f7",
  gray200: "#eef0f1",
  gray300: "#e5e7eb",
  text: "#111827",
  sub: "#6b7280",
};

// ---- Styles
const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 24,
    // reserve space so content never sits under the fixed footer
    paddingBottom: FOOTER_HEIGHT + FOOTER_MARGIN + 20,
    fontSize: 12,
    fontFamily: "Helvetica",
    color: C.text,
  },

  // Header
  header: {
    fontSize: 22,
    marginBottom: 10,
    fontWeight: 700,
    textAlign: "center",
    color: C.green,
  },

  // Cards / sections
  sectionCard: {
    borderWidth: 1,
    borderColor: C.gray200,
    backgroundColor: "#fff",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, marginBottom: 8 },

  // Body text
  line: { fontSize: 12, marginBottom: 4 },
  kv: { fontSize: 12, marginBottom: 4, paddingLeft: 8 },

  // Pills / chips
  pill: {
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 10,
    fontSize: 11,
    fontWeight: 700,
    alignSelf: "flex-start",
  },
  yes: { backgroundColor: C.greenDark, color: "#fff" },
  no: { backgroundColor: C.red, color: "#fff" },

  // Table
  table: {
    borderWidth: 1,
    borderColor: C.gray300,
    borderRadius: 8,
    overflow: "hidden",
    marginTop: 8,
  },
  thead: {
    flexDirection: "row",
    backgroundColor: C.gray100,
    borderBottomWidth: 1,
    borderBottomColor: C.gray300,
  },
  th: { flex: 1, fontSize: 11, fontWeight: 700, paddingVertical: 8, paddingHorizontal: 10 },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: C.gray300,
  },
  td: { flex: 1, fontSize: 11, paddingVertical: 8, paddingHorizontal: 10 },

  // Footer
  footer: {
    position: "absolute",
    bottom: FOOTER_MARGIN,
    left: 24,
    right: 24,
    height: FOOTER_HEIGHT,
    borderTopWidth: 1,
    borderTopColor: C.gray300,
    paddingTop: 6,
    fontSize: 9,
    color: C.sub,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLogo: { width: 60 },
});

// ---- Footer
const Footer = ({ }) => {
  const dateText = new Date().toLocaleDateString("en-US");
  return (
    <View style={styles.footer} fixed>
      <Text>{`Generated by Portend AI • Confidential • ${dateText}`}</Text>
      {logo ? <Image style={styles.footerLogo} src={logo} /> : null}
      {/* If you imported a static logo, swap to: <Image style={styles.footerLogo} src={logo} /> */}
    </View>
  );
};

// ---- PDF Component
const SSLReportPDF = ({ data, pageTitle = "SSL/TLS Report", footerLogo }) => {
  const r = data?.result || {};
  const serverInfo = r.ServerInformation || {};
  const cert = r.CertificateAnalysis || {};
  const chain = r.CertificateChainValidation?.certificateChain || [];
  const ciphers = r.SupportedCiphersAndProtocols || {};
  const vulns = r.VulnerabilityAssessments || {};
  const keyex = r.KeyExchange || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <Text style={styles.header}>{pageTitle}</Text>

        {/* Target & Report Date */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Target</Text>
          <Text style={styles.line}>
            Hostname: <Text style={{ fontWeight: 700 }}>{serverInfo.hostname || "-"}</Text>
          </Text>
          <Text style={{ fontSize: 11, color: C.sub }}>
            Report Date: {moment(data?.date).format("MMM DD, YYYY HH:mm")}
          </Text>
        </View>

        {/* Server Information */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Server Information</Text>
          {Object.entries(serverInfo).map(([k, v]) => (
            <Text key={k} style={styles.kv}>
              {k.replace(/([A-Z])/g, " $1").trim()}: {String(v)}
            </Text>
          ))}
        </View>

        {/* Certificate Analysis */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Certificate Analysis</Text>
          <Text style={styles.kv}>Common Name: {cert?.commonName || "-"}</Text>
          <Text style={styles.kv}>
            Issuer: {cert?.issuer?.organization || "-"}
            {cert?.issuer?.country ? `, ${cert.issuer.country}` : ""}{" "}
            {cert?.issuer?.name ? `(${cert.issuer.name})` : ""}
          </Text>
          <Text style={styles.kv}>
            Validity Period:{" "}
            {cert?.validityPeriod?.from
              ? moment(cert.validityPeriod.from).format("MMM DD, YYYY")
              : "-"}
            {"  -  "}
            {cert?.validityPeriod?.to
              ? moment(cert.validityPeriod.to).format("MMM DD, YYYY")
              : "-"}
          </Text>
          <Text style={styles.kv}>
            Key Info: {cert?.keyInfo?.type || "-"}
            {cert?.keyInfo?.size ? ` (${cert.keyInfo.size} bits)` : ""}
          </Text>
          <Text style={styles.kv}>
            Subject Alternative Names: {(cert?.subjectAlternativeNames || []).join(", ") || "-"}
          </Text>
        </View>

        {/* Certificate Chain */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Certificate Chain Validation</Text>
          <View style={styles.table}>
            <View style={styles.thead}>
              <Text style={styles.th}>Name</Text>
              <Text style={styles.th}>Organization</Text>
              <Text style={styles.th}>Valid From</Text>
              <Text style={styles.th}>Valid To</Text>
            </View>
            {chain.map((c, i) => (
              <View key={i} style={styles.row}>
                <Text style={styles.td}>{c.name}</Text>
                <Text style={styles.td}>{c.organization}</Text>
                <Text style={styles.td}>
                  {c.validityPeriod?.from ? moment(c.validityPeriod.from).format("MMM DD, YYYY") : "-"}
                </Text>
                <Text style={styles.td}>
                  {c.validityPeriod?.to ? moment(c.validityPeriod.to).format("MMM DD, YYYY") : "-"}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Supported Ciphers & Protocols */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Supported Ciphers & Protocols</Text>
          <View style={styles.table}>
            <View style={styles.thead}>
              <Text style={styles.th}>Protocol</Text>
              <Text style={styles.th}>Supported</Text>
            </View>
            {Object.entries(ciphers).map(([k, v]) => (
              <View key={k} style={styles.row}>
                <Text style={styles.td}>{k}</Text>
                <View style={[styles.td, { flexDirection: "row" }]}>
                  <Text style={[styles.pill, v?.supported ? styles.yes : styles.no]}>
                    {v?.supported ? "Yes" : "No"}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Vulnerability Assessments */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Vulnerability Assessments</Text>
          {Object.entries(vulns).map(([k, v]) => (
            <View key={k} style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: 12 }}>
                {k.replace(/([A-Z])/g, " $1").trim()}:
              </Text>
              <Text style={[styles.pill, v ? styles.yes : styles.no]}>{v ? "Safe" : "Vulnerable"}</Text>
            </View>
          ))}
        </View>

        {/* Key Exchange */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Key Exchange</Text>
          <Text style={styles.kv}>
            ECDH Supported:{" "}
            <Text style={[styles.pill, keyex?.ecdhSupported ? styles.yes : styles.no]}>
              {keyex?.ecdhSupported ? "Yes" : "No"}
            </Text>
          </Text>
          <Text style={styles.kv}>
            Supported Curves: {(keyex?.supportedCurves || []).map((c) => c.name).join(", ") || "-"}
          </Text>
          <Text style={styles.kv}>
            Rejected Curves: {(keyex?.rejectedCurves || []).map((c) => c.name).join(", ") || "-"}
          </Text>
        </View>

        {/* Spacer to guarantee no overlap with the fixed footer */}
        <View style={{ height: FOOTER_HEIGHT + FOOTER_MARGIN }} />

        {/* Fixed Footer */}
        <Footer />
      </Page>
    </Document>
  );
};

export default SSLReportPDF;
